// src/routes/Home.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";

import Layout from "../components/Layout";
import ProductCard from "../components/ProductCard";
import FilterBar, { applyFilters } from "../components/FilterBar";
import useReveal from "../hooks/useReveal";
import { getProducts, getCategories, displayName } from "../services/db";

gsap.registerPlugin(ScrollTrigger);

/* Real shades from the LaBina Farbkarte. Used for the hero colour card
   before Firestore loads, and as a fallback if the catalogue is empty. */
const CARD_SHADES = [
  { hex: "#D8B98C", nameSq: "Bionde" },
  { hex: "#9C7A55", nameSq: "Bionde e errët" },
  { hex: "#B5702A", nameSq: "Kafe e çelët" },
  { hex: "#6E4A24", nameSq: "Kafe mesatare" },
  { hex: "#3B2B1E", nameSq: "Kafe e errët" },
  { hex: "#241A14", nameSq: "Kafe e zezë" },
  { hex: "#8C8C8C", nameSq: "Gri e çelët" },
  { hex: "#1A1A1A", nameSq: "E zezë intensive" },
  { hex: "#B87C7C", nameSq: "Rozë e vjetër" },
  { hex: "#E8618F", nameSq: "Rozë pudër" },
  { hex: "#D4295A", nameSq: "E kuqe trëndafili" },
  { hex: "#E33C22", nameSq: "E kuqe Mars" },
  { hex: "#B01836", nameSq: "E kuqe qershie" },
  { hex: "#6E1436", nameSq: "Bordo" },
  { hex: "#C1341F", nameSq: "Terrakotë" },
  { hex: "#E5B2A0", nameSq: "Nuancë lëkure rozë" },
  { hex: "#1B5E3A", nameSq: "Jeshile ulliri" },
  { hex: "#1F3A73", nameSq: "Blu mesnate" },
  { hex: "#4B2E6B", nameSq: "Vjollcë ultra" },
];

const PROOF = [
  "Made in Germany",
  "Pa okside hekuri",
  "Pa alkool",
  "novoxx\u00ae",
];

/* Grounded in the LaBina certificate, not invented marketing numbers. */
const STATS = [
  { value: "2004", label: "Prodhim gjerman që nga" },
  { value: "18", label: "Parametra laboratorikë të testuar" },
  { value: "0", label: "Okside hekuri në përbërje" },
  { value: "100%", label: "Pa konservues kimikë" },
];

const TRUST = [
  {
    title: "Pa okside hekuri",
    body: "Vetullat nuk skuqen me kalimin e kohës dhe rreziku i reaksioneve alergjike bie ndjeshëm.",
  },
  {
    title: "novoxx® në vend të konservuesve",
    body: "Biokompatibil 100%. Trupi nuk e njeh si substancë të huaj dhe shishja mbetet pa mikrobe pas hapjes.",
  },
  {
    title: "Pa alkool",
    body: "Alkooli irriton lëkurën dhe djeg në plagë. Ngjyrat LaBina® nuk përmbajnë asnjë sasi.",
  },
  {
    title: "Të sigurta gjatë MRI",
    body: "Pa efekte anësore gjatë rezonancës magnetike, ndryshe nga pigmentet me okside hekuri.",
  },
];

export default function Home() {
  const heroRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sort, setSort] = useState("code");

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, c] = await Promise.all([getProducts(), getCategories()]);
        if (!alive) return;
        setProducts(p.filter((x) => x.active !== false));
        setCategories(c);
      } catch (err) {
        console.error("Home load failed", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* Hero shade card: entrance, then auto-advance until the user takes over. */
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      tl.from(".hero-line", { yPercent: 112, duration: 0.9, stagger: 0.07 })
        .from(
          ".hero-card",
          { opacity: 0, scale: 0.94, y: 24, duration: 0.9 },
          "-=0.65",
        )
        .from(
          ".hero-fade",
          { opacity: 0, y: 14, duration: 0.7, stagger: 0.08 },
          "-=0.6",
        )
        .from(
          ".rail-dot",
          {
            opacity: 0,
            scale: 0.4,
            duration: 0.4,
            stagger: 0.025,
            ease: "back.out(2)",
          },
          "-=0.5",
        );

      /* Card drifts up slightly as you scroll away — depth without parallax jank */
      gsap.to(".hero-card", {
        yPercent: -8,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "bottom top",
          scrub: 0.8,
        },
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const shades = useMemo(() => {
    const fromDb = products
      .filter((p) => p.hex)
      .slice(0, 14)
      .map((p) => ({
        id: p.id,
        hex: p.hex,
        nameSq: displayName(p),
        pmuCode: p.pmuCode,
        mbCode: p.mbCode,
        image: p.images?.[0] || null,
      }));
    return fromDb.length >= 6 ? fromDb : CARD_SHADES.slice(0, 14);
  }, [products]);

  const activeShade = shades[Math.min(active, shades.length - 1)];

  /* Auto-advance stops permanently once someone picks a shade themselves. */
  useEffect(() => {
    if (paused || shades.length < 2) return;
    const id = setInterval(
      () => setActive((i) => (i + 1) % shades.length),
      3600,
    );
    return () => clearInterval(id);
  }, [paused, shades.length]);

  const filtered = useMemo(
    () => applyFilters(products, { search, categoryId, sort }),
    [products, search, categoryId, sort],
  );

  const visible = filtered.slice(0, 8);
  const visibleCount = visible.length;

  const scope = useReveal({ deps: [loading, visibleCount] });

  return (
    <Layout padTop={false}>
      <div ref={scope}>
        {/* ---------------- hero ---------------- */}
        <Hero ref={heroRef}>
          <HeroGrid>
            <HeroCopy>
              <Eyebrow className="hero-fade">
                <Dot />
                Distributor ekskluziv i LaBina<sup>&reg;</sup> për Kosovë
              </Eyebrow>

              <Headline>
                <Mask>
                  <span className="hero-line">Çdo nuancë</span>
                </Mask>
                <Mask>
                  <span className="hero-line">ka një</span>
                </Mask>
                <Mask>
                  <span className="hero-line">
                    <Accent>histori.</Accent>
                  </span>
                </Mask>
              </Headline>

              <HeroText className="hero-fade">
                Pigmente të prodhuara në Gjermani për Permanent Make-up dhe
                Microblading. Pa okside hekuri, pa alkool, pa konservues kimikë.
              </HeroText>

              <HeroActions className="hero-fade">
                <Primary to="/produktet">
                  Shiko kartelën e ngjyrave <FiArrowRight />
                </Primary>
                <Secondary to="/rreth-nesh">
                  Pse LaBina<sup>&reg;</sup>
                </Secondary>
              </HeroActions>

              <Proof className="hero-fade" data-reveal-stagger>
                {PROOF.map((p) => (
                  <ProofPill key={p}>{p}</ProofPill>
                ))}
              </Proof>
            </HeroCopy>

            {/* The signature element: a live shade card, not a static banner */}
            <ShadeStage
              className="hero-card"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <StageCard
                as={activeShade?.id ? Link : "div"}
                to={
                  activeShade?.id ? `/produktet/${activeShade.id}` : undefined
                }
                $hex={activeShade?.hex}
              >
                <Counter>
                  <strong>{String(active + 1).padStart(2, "0")}</strong>
                  <span>/ {String(shades.length).padStart(2, "0")}</span>
                </Counter>

                {activeShade?.image && (
                  <StageImage src={activeShade.image} alt="" loading="eager" />
                )}

                <StageInfo>
                  <StageMeta>
                    {activeShade?.pmuCode && (
                      <MetaCode>{activeShade.pmuCode}</MetaCode>
                    )}
                    {activeShade?.mbCode && (
                      <MetaCode $mb>{activeShade.mbCode}</MetaCode>
                    )}
                  </StageMeta>
                  <StageName key={activeShade?.nameSq}>
                    {activeShade?.nameSq}
                  </StageName>
                  {activeShade?.id && (
                    <StageLink>
                      Shiko nuancën <FiArrowUpRight />
                    </StageLink>
                  )}
                </StageInfo>

                <Progress $paused={paused} key={`p-${active}-${paused}`} />
              </StageCard>

              <Rail role="tablist" aria-label="Zgjidhni nuancën">
                {shades.map((s, i) => (
                  <RailDot
                    key={`${s.hex}-${i}`}
                    role="tab"
                    aria-selected={i === active}
                    aria-label={s.nameSq}
                    title={s.nameSq}
                    $hex={s.hex}
                    $active={i === active}
                    onClick={() => {
                      setActive(i);
                      setPaused(true);
                    }}
                  />
                ))}
              </Rail>
            </ShadeStage>
          </HeroGrid>

          <ScrollCue className="hero-fade" aria-hidden="true">
            <span>Zbrit</span>
            <CueLine />
          </ScrollCue>
        </Hero>

        {/* ---------------- stats ---------------- */}
        <Stats data-reveal-stagger>
          <StatsInner>
            {STATS.map((s) => (
              <Stat key={s.label} className="reveal">
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </Stat>
            ))}
          </StatsInner>
        </Stats>

        {/* ---------------- products ---------------- */}
        <Section>
          <SectionHead>
            <div>
              <SectionEyebrow className="reveal">Katalogu</SectionEyebrow>
              <h2 className="reveal">Gjeni nuancën tuaj</h2>
            </div>
            <SeeAll to="/produktet" className="reveal">
              Të gjitha produktet <FiArrowRight />
            </SeeAll>
          </SectionHead>

          <FilterWrap className="reveal">
            <FilterBar
              search={search}
              onSearch={setSearch}
              categories={categories}
              categoryId={categoryId}
              onCategory={setCategoryId}
              sort={sort}
              onSort={setSort}
              resultCount={filtered.length}
            />
          </FilterWrap>

          {loading ? (
            <Grid>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} />
              ))}
            </Grid>
          ) : visible.length ? (
            <Grid data-reveal-stagger>
              {visible.map((p) => (
                <ProductCard key={p.id} product={p} className="reveal" />
              ))}
            </Grid>
          ) : (
            <EmptyState className="reveal">
              <p>Asnjë produkt nuk përputhet me kërkimin tuaj.</p>
              <button
                onClick={() => {
                  setSearch("");
                  setCategoryId("");
                }}
              >
                Pastro filtrat
              </button>
            </EmptyState>
          )}
        </Section>

        {/* ---------------- trust ---------------- */}
        <Trust>
          <TrustInner>
            <TrustHead>
              <SectionEyebrow className="reveal">Pse LaBina®</SectionEyebrow>
              <h2 className="reveal">Siguria nuk është opsion shtesë</h2>
              <TrustLede className="reveal">
                Çdo ngjyrë shoqërohet me deklaratë konformiteti sipas
                Rregullores (BE) 2020/2081, e disponueshme edhe për klientin
                përfundimtar.
              </TrustLede>
            </TrustHead>

            <TrustGrid data-reveal-stagger>
              {TRUST.map((t) => (
                <TrustItem key={t.title} className="reveal">
                  <h3>{t.title}</h3>
                  <p>{t.body}</p>
                </TrustItem>
              ))}
            </TrustGrid>
          </TrustInner>
        </Trust>

        {/* ---------------- cta ---------------- */}
        <Cta>
          <CtaInner className="reveal">
            <h2>Keni pyetje për nuancat?</h2>
            <p>
              Na shkruani se çfarë tipi lëkure dhe rezultat kërkoni, dhe ju
              ndihmojmë të zgjidhni nuancën e duhur para se të porosisni.
            </p>
            <Primary to="/kontakt">
              Na kontaktoni <FiArrowRight />
            </Primary>
          </CtaInner>
        </Cta>
      </div>
    </Layout>
  );
}

/* ------------------------------------------------------------------ */
/*  hero                                                               */
/* ------------------------------------------------------------------ */

const Hero = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: calc(${({ theme }) => theme.layout.navHeight} + 48px)
    ${({ theme }) => theme.layout.gutter} 72px;
  overflow: hidden;

  /* Soft brand wash behind the card instead of a hard colour band */
  &::before {
    content: "";
    position: absolute;
    top: -18%;
    right: -12%;
    width: 62vw;
    height: 62vw;
    max-width: 900px;
    max-height: 900px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${({ theme }) => theme.color.brandSoft} 0%,
      rgba(255, 255, 255, 0) 68%
    );
    pointer-events: none;
  }
`;

const HeroGrid = styled.div`
  position: relative;
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  align-items: center;
  gap: clamp(40px, 6vw, 90px);

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const HeroCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 26px;
  padding: 7px 16px 7px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.color.line};
  background: ${({ theme }) => theme.color.bg};
  font-size: ${({ theme }) => theme.size.xs};
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.inkSoft};

  sup {
    font-size: 0.6em;
  }
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.brand};
  flex-shrink: 0;

  @media (prefers-reduced-motion: no-preference) {
    animation: pulse 2.4s ${({ theme }) => theme.ease.inOut} infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(225, 29, 72, 0.35);
    }
    50% {
      box-shadow: 0 0 0 6px rgba(225, 29, 72, 0);
    }
  }
`;

const Headline = styled.h1`
  margin: 0;
  max-width: 13ch;
`;

const Mask = styled.span`
  display: block;
  overflow: hidden;
  padding-bottom: 0.04em;

  span {
    display: block;
  }
`;

const Accent = styled.em`
  font-style: normal;
  color: ${({ theme }) => theme.color.brand};
`;

const HeroText = styled.p`
  margin: 28px 0 0;
  max-width: 46ch;
  font-size: ${({ theme }) => theme.size.lg};
  color: ${({ theme }) => theme.color.body};
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 34px;
`;

const Primary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 28px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.ink};
  color: #fff;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  transition:
    background 0.3s,
    transform 0.3s ${({ theme }) => theme.ease.out},
    box-shadow 0.3s;

  svg {
    transition: transform 0.3s ${({ theme }) => theme.ease.out};
  }

  &:hover {
    background: ${({ theme }) => theme.color.brand};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadow.md};
    svg {
      transform: translateX(4px);
    }
  }
`;

const Secondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 52px;
  padding: 0 28px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.color.line};
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.ink};
  transition: all 0.28s ${({ theme }) => theme.ease.out};

  sup {
    font-size: 0.6em;
  }

  &:hover {
    border-color: ${({ theme }) => theme.color.ink};
    background: ${({ theme }) => theme.color.bgSoft};
  }
`;

const Proof = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 38px;
  padding-top: 28px;
  border-top: 1px solid ${({ theme }) => theme.color.lineSoft};
`;

const ProofPill = styled.li`
  padding: 6px 13px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.bgSoft};
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.body};
  white-space: nowrap;
`;

/* ---------------- shade card ---------------- */

const ShadeStage = styled.div`
  min-width: 0;

  ${({ theme }) => theme.down.lg} {
    max-width: 460px;
    width: 100%;
  }
`;

const StageCard = styled.div`
  position: relative;
  display: block;
  aspect-ratio: 4 / 5;
  border-radius: ${({ theme }) => theme.radius.xl};
  background: ${({ $hex }) => $hex || "#ddd"};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadow.lg};
  transition:
    background 0.7s ${({ theme }) => theme.ease.out},
    transform 0.4s ${({ theme }) => theme.ease.out};

  &:hover {
    transform: translateY(-4px);
  }

  ${({ theme }) => theme.down.md} {
    aspect-ratio: 1 / 1;
  }
`;

const StageImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* The shade colour stays visible behind the bottle instead of being
     cropped away by a cover fill. */
  object-fit: contain;
  object-position: center;
  padding: 16% 16% 32%;
`;

const Counter = styled.div`
  position: absolute;
  top: 22px;
  right: 24px;
  z-index: 2;
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: rgba(255, 255, 255, 0.95);
  font-family: ${({ theme }) => theme.font.display};
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.28);

  strong {
    font-size: 1.5rem;
    font-weight: ${({ theme }) => theme.weight.medium};
    letter-spacing: -0.03em;
  }
  span {
    font-size: 0.78rem;
    opacity: 0.8;
  }
`;

/* Frosted panel — legible over any pigment, from Weiss to Tiefschwarz */
const StageInfo = styled.div`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;
  z-index: 2;
  padding: 20px 22px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
`;

const StageMeta = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  min-height: 20px;
`;

const MetaCode = styled.span`
  padding: 3px 9px;
  border-radius: ${({ theme }) => theme.radius.xs};
  font-size: 0.66rem;
  font-weight: ${({ theme }) => theme.weight.semi};
  letter-spacing: 0.03em;
  background: ${({ $mb, theme }) =>
    $mb ? theme.color.brandSoft : "rgba(12,10,9,0.06)"};
  color: ${({ $mb, theme }) =>
    $mb ? theme.color.brandDark : theme.color.body};
`;

const StageName = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem);
  font-weight: ${({ theme }) => theme.weight.medium};
  letter-spacing: -0.035em;
  line-height: 1.1;
  color: ${({ theme }) => theme.color.ink};

  @media (prefers-reduced-motion: no-preference) {
    animation: swap 0.5s ${({ theme }) => theme.ease.out};
  }

  @keyframes swap {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StageLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 12px;
  font-size: ${({ theme }) => theme.size.xs};
  font-weight: ${({ theme }) => theme.weight.semi};
  color: ${({ theme }) => theme.color.brand};

  svg {
    transition: transform 0.3s ${({ theme }) => theme.ease.out};
  }

  ${StageCard}:hover & svg {
    transform: translate(3px, -3px);
  }
`;

const Progress = styled.span`
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 3;
  height: 3px;
  width: 100%;
  transform-origin: left;
  background: rgba(255, 255, 255, 0.9);

  @media (prefers-reduced-motion: no-preference) {
    animation: fill 3.6s linear forwards;
    animation-play-state: ${({ $paused }) => ($paused ? "paused" : "running")};
  }
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }

  @keyframes fill {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }
`;

const Rail = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
`;

const RailDot = styled.button.attrs({ className: "rail-dot" })`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${({ $hex }) => $hex};
  position: relative;
  flex-shrink: 0;
  transition: transform 0.3s ${({ theme }) => theme.ease.spring};
  box-shadow: inset 0 0 0 1px rgba(12, 10, 9, 0.08);

  &::after {
    content: "";
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    border: 1.5px solid ${({ theme }) => theme.color.ink};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transform: scale(${({ $active }) => ($active ? 1 : 0.8)});
    transition:
      opacity 0.3s,
      transform 0.3s ${({ theme }) => theme.ease.spring};
  }

  &:hover {
    transform: scale(1.14);
  }
`;

const ScrollCue = styled.div`
  position: absolute;
  left: 50%;
  bottom: 26px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 0.68rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.muted};

  ${({ theme }) => theme.down.lg} {
    display: none;
  }
`;

const CueLine = styled.span`
  width: 1px;
  height: 42px;
  background: linear-gradient(${({ theme }) => theme.color.line}, transparent);
`;

/* ------------------------------------------------------------------ */
/*  stats                                                              */
/* ------------------------------------------------------------------ */

const Stats = styled.section`
  background: ${({ theme }) => theme.color.bgDark};
  color: #fff;
`;

const StatsInner = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 64px ${({ theme }) => theme.layout.gutter};
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;

  ${({ theme }) => theme.down.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 34px;
  }
`;

const Stat = styled.div``;

const StatValue = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(2.2rem, 1.4rem + 2.4vw, 3.4rem);
  line-height: 1;
  color: ${({ theme }) => theme.color.brand};
`;

const StatLabel = styled.p`
  margin: 12px 0 0;
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
  max-width: 22ch;
`;

/* ------------------------------------------------------------------ */
/*  products                                                           */
/* ------------------------------------------------------------------ */

const Section = styled.section`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 110px ${({ theme }) => theme.layout.gutter};
`;

const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;

  ${({ theme }) => theme.down.md} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SectionEyebrow = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  font-weight: ${({ theme }) => theme.weight.semi};
`;

const SeeAll = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 4px;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.semi};
  color: ${({ theme }) => theme.color.ink};
  border-bottom: 1px solid ${({ theme }) => theme.color.line};
  transition:
    color 0.25s,
    border-color 0.25s;

  &:hover {
    color: ${({ theme }) => theme.color.brand};
    border-color: ${({ theme }) => theme.color.brand};
  }
`;

const FilterWrap = styled.div`
  margin-bottom: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.color.lineSoft};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: repeat(3, 1fr);
  }
  ${({ theme }) => theme.down.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const Skeleton = styled.div`
  aspect-ratio: 1 / 1.28;
  border-radius: ${({ theme }) => theme.radius.md};
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.color.bgSoft} 30%,
    ${({ theme }) => theme.color.lineSoft} 50%,
    ${({ theme }) => theme.color.bgSoft} 70%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s infinite linear;

  @keyframes shimmer {
    to {
      background-position: -220% 0;
    }
  }
`;

const EmptyState = styled.div`
  padding: 70px 0;
  text-align: center;
  color: ${({ theme }) => theme.color.muted};

  button {
    margin-top: 10px;
    color: ${({ theme }) => theme.color.brand};
    font-weight: ${({ theme }) => theme.weight.semi};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

/* ------------------------------------------------------------------ */
/*  trust                                                              */
/* ------------------------------------------------------------------ */

const Trust = styled.section`
  background: ${({ theme }) => theme.color.bgSoft};
  border-top: 1px solid ${({ theme }) => theme.color.lineSoft};
`;

const TrustInner = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 110px ${({ theme }) => theme.layout.gutter};
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 70px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr;
    gap: 44px;
  }
`;

const TrustHead = styled.div``;

const TrustLede = styled.p`
  margin: 20px 0 0;
  max-width: 40ch;
  color: ${({ theme }) => theme.color.body};
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 34px 40px;

  ${({ theme }) => theme.down.sm} {
    grid-template-columns: 1fr;
  }
`;

const TrustItem = styled.div`
  padding-top: 22px;
  border-top: 2px solid ${({ theme }) => theme.color.brand};

  h3 {
    margin: 0 0 10px;
    font-family: ${({ theme }) => theme.font.sans};
    font-size: ${({ theme }) => theme.size.lg};
    font-weight: ${({ theme }) => theme.weight.semi};
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.size.sm};
  }
`;

/* ------------------------------------------------------------------ */
/*  cta                                                                */
/* ------------------------------------------------------------------ */

const Cta = styled.section`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 110px ${({ theme }) => theme.layout.gutter};
`;

const CtaInner = styled.div`
  text-align: center;

  p {
    margin: 18px auto 32px;
    max-width: 52ch;
    font-size: ${({ theme }) => theme.size.lg};
  }
`;
